import React, { useEffect, useState, useRef } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import useAuthStore from '../hooks/useAuth';
import NotificationBanner from '../components/NotificationBanner';
import { playNotificationSound } from '../push';

const navItems = [
  { to: '/portal', label: 'Dashboard', icon: '📊', end: true },
  { to: '/portal/mails', label: 'E-Mails', icon: '✉️' },
  { to: '/portal/gesendet', label: 'Gesendet', icon: '📤' },
  { to: '/portal/spam', label: 'Spam', icon: '🚫' },
  { to: '/portal/berichte', label: 'Berichte', icon: '📄' },
  { to: '/portal/einstellungen', label: 'Einstellungen', icon: '⚙️' },
];

export default function PortalLayout() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const closeMobile = () => setMobileOpen(false);

  // Solange das Portal offen ist: bei eingehender Push-Nachricht (App im
  // Vordergrund) einen eigenen Hinweiston abspielen. Im Hintergrund/geschlossen
  // übernimmt stattdessen der System-Benachrichtigungston des Geräts.
  // Zusätzlich ein Browser-weites Event auslösen, auf das einzelne Seiten
  // (z.B. PortalMails, PortalDashboard) hören können, um ihre Daten neu zu
  // laden — vorher wurde bei eingehendem Push/Klick auf die Benachrichtigung
  // nur der Ton abgespielt bzw. das Fenster fokussiert, ohne dass die
  // Mail-Liste tatsächlich aktualisiert wurde.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handleMessage = (event) => {
      const type = event.data?.type;
      if (type === 'NEW_MAIL_PUSH') {
        playNotificationSound();
      }
      if (type === 'NEW_MAIL_PUSH' || type === 'REFRESH_MAILS') {
        window.dispatchEvent(new CustomEvent('incomail:refresh-mails'));
      }
    };
    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
  }, []);

  // Sicherheitsnetz unabhängig vom Service-Worker-Timing: das postMessage
  // oben setzt voraus, dass genau der richtige Client gefunden wird UND der
  // Browser bereits den neuesten Service-Worker aktiv geschaltet hat (nach
  // einem Rebuild kann das etwas dauern/hakelig sein). Deshalb zusätzlich:
  // sobald der Tab wieder sichtbar wird oder Fokus bekommt (z.B. weil man
  // per Klick auf die System-Benachrichtigung in den Browser zurückspringt),
  // die Mail-Liste ebenfalls neu laden. Debounce verhindert Spam bei
  // schnellem Tab-/App-Wechsel.
  const lastVisibilityRefreshRef = useRef(0);
  useEffect(() => {
    const triggerRefresh = () => {
      const now = Date.now();
      if (now - lastVisibilityRefreshRef.current < 5000) return;
      lastVisibilityRefreshRef.current = now;
      window.dispatchEvent(new CustomEvent('incomail:refresh-mails'));
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') triggerRefresh();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', triggerRefresh);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', triggerRefresh);
    };
  }, []);

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Abdunkelung hinter dem mobilen Menü */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 md:hidden"
          onClick={closeMobile}
        />
      )}

      {/* Seitenmenü — auf Mobile ein einfahrbares Overlay, ab md: fest sichtbar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 flex flex-col
          transform transition-transform duration-200 ease-in-out
          md:static md:translate-x-0 md:w-56
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <img src="/logo-wide.png" alt="IncoMail" className="w-full h-auto max-w-[190px] object-contain" />
            <div className="text-xs text-gray-400 truncate max-w-[180px] mt-1">{user?.customer_name}</div>
          </div>
          <button
            onClick={closeMobile}
            className="md:hidden text-gray-400 hover:text-gray-600 p-1 flex-shrink-0"
            aria-label="Menü schließen"
          >
            ✕
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map(({ to, label, icon, end }) => (
            <NavLink key={to} to={to} end={end} onClick={closeMobile}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
                }`
              }
            >
              <span>{icon}</span>{label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-gray-100">
          <div className="text-xs text-gray-400 mb-2 px-2 truncate">{user?.email}</div>
          <button onClick={handleLogout}
            className="w-full px-3 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg text-left">
            Abmelden
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Mobile-Kopfleiste mit Hamburger-Button */}
        <div className="md:hidden flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200 flex-shrink-0">
          <button
            onClick={() => setMobileOpen(true)}
            className="text-gray-600 hover:text-gray-900 p-1"
            aria-label="Menü öffnen"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <div className="min-w-0">
            <img src="/logo-wide.png" alt="IncoMail" className="h-6 object-contain" />
          </div>
        </div>

        <main className="flex-1 overflow-auto">
          <NotificationBanner />
          {user?.customer_status === 'paused' && (
            <div className="bg-amber-50 border-b border-amber-200 px-4 md:px-6 py-3 flex items-center gap-3">
              <span className="text-amber-500 text-lg">⚠️</span>
              <div>
                <span className="text-sm font-medium text-amber-800">Konto pausiert — </span>
                <span className="text-sm text-amber-700">
                  Der E-Mail-Abruf ist derzeit deaktiviert. Bereits empfangene Mails sind weiterhin einsehbar.
                  Bitte wenden Sie sich an den Administrator, um das Konto wieder zu aktivieren.
                </span>
              </div>
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
